import clock from "./clock";

export interface AdvanceTarget {
    ids: string[];
    nextStep?: number;
}

export interface GuidanceStep {
    advanceOn: AdvanceTarget[];
    action: () => void;
}

export interface GuidanceSequence {
    steps: GuidanceStep[];
}

type ClickRecord = { el: SVGElement; handler: EventListener };

const elementRegistry = new Map<string, SVGElement>();

export function registerElement(id: string, el: SVGElement): void {
    elementRegistry.set(id, el);
}

class GuidanceManager {
    private highlightEls: SVGElement[] = [];
    private lastHighlightedIds: string[] = [];
    private tooltipEl: HTMLElement;
    private currentSequence: GuidanceStep[] | null = null;
    private currentStepIndex = 0;
    private clickRecords: ClickRecord[] = [];

    constructor() {
        this.tooltipEl = document.createElement("div");
        this.tooltipEl.id = "guidance-tooltip";
        this.tooltipEl.style.display = "none";
        document.body.appendChild(this.tooltipEl);
    }

    clearRegistry(): void {
        elementRegistry.clear();
    }

    addGuidance(title: string, sequence: GuidanceSequence): void {
        const container = document.getElementById("how-to-buttons")!;
        const btn = document.createElement("button");
        btn.className = "how-to-button";
        btn.textContent = title;
        btn.addEventListener("click", () => this.start(sequence));
        container.appendChild(btn);
    }

    start(sequence: GuidanceSequence): void {
        this.stop();
        this.currentSequence = [...sequence.steps];
        this.currentStepIndex = 0;
        this.showStep();
    }

    stop(): void {
        this.removeClickHandlers();
        this.currentSequence = null;
        this.currentStepIndex = 0;
        this.clearHighlights();
    }

    highlight(ids: string[]): void {
        this.clearHighlights();
        this.lastHighlightedIds = ids;
        for (const id of ids) {
            const el = elementRegistry.get(id);
            if (el) this.addElementHighlight(el);
        }
    }

    showMessage(message: string): void {
        const els = this.lastHighlightedIds
            .map((id) => elementRegistry.get(id))
            .filter((el): el is SVGElement => !!el);
        if (els.length > 0) this.positionTooltip(els, message);
    }

    private showStep(): void {
        if (
            !this.currentSequence ||
            this.currentStepIndex >= this.currentSequence.length
        ) {
            this.stop();
            return;
        }

        const step = this.currentSequence[this.currentStepIndex];
        step.action();

        for (const target of step.advanceOn) {
            const advance = () => {
                this.removeClickHandlers();
                this.currentStepIndex =
                    target.nextStep !== undefined
                        ? target.nextStep
                        : this.currentStepIndex + 1;
                setTimeout(() => this.showStep(), 100);
            };
            for (const id of target.ids) {
                const el = elementRegistry.get(id);
                if (!el) continue;
                this.clickRecords.push({ el, handler: advance });
                el.addEventListener("click", advance);
            }
        }
    }

    private removeClickHandlers(): void {
        for (const { el, handler } of this.clickRecords) {
            el.removeEventListener("click", handler);
        }
        this.clickRecords = [];
    }

    private addElementHighlight(el: SVGElement): void {
        const x = parseFloat(el.getAttribute("x") || "0");
        const y = parseFloat(el.getAttribute("y") || "0");
        const w = parseFloat(el.getAttribute("width") || "0");
        const h = parseFloat(el.getAttribute("height") || "0");
        const rx = parseFloat(el.getAttribute("rx") || "10");

        const svg = document.getElementById("panel")!;
        const ns = "http://www.w3.org/2000/svg";
        const rect = document.createElementNS(ns, "rect") as SVGRectElement;
        rect.setAttribute("x", (x - 6).toString());
        rect.setAttribute("y", (y - 6).toString());
        rect.setAttribute("width", (w + 12).toString());
        rect.setAttribute("height", (h + 12).toString());
        rect.setAttribute("rx", (rx + 4).toString());
        rect.setAttribute("ry", (rx + 4).toString());
        rect.setAttribute("fill", "rgba(255, 255, 0, 0.2)");
        rect.setAttribute("stroke", "yellow");
        rect.setAttribute("stroke-width", "5");
        rect.setAttribute("pointer-events", "none");
        rect.setAttribute("class", "guidance-highlight");
        svg.appendChild(rect);
        this.highlightEls.push(rect);
    }

    private clearHighlights(): void {
        for (const el of this.highlightEls) el.remove();
        this.highlightEls = [];
        this.lastHighlightedIds = [];
        this.tooltipEl.style.display = "none";
    }

    private positionTooltip(els: SVGElement[], message: string): void {
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
        for (const el of els) {
            const r = el.getBoundingClientRect();
            minX = Math.min(minX, r.left);
            minY = Math.min(minY, r.top);
            maxX = Math.max(maxX, r.right);
            maxY = Math.max(maxY, r.bottom);
        }
        const centerX = (minX + maxX) / 2;

        const tooltipWidth = 220;
        const tooltipApproxHeight = 56;
        const placeAbove = minY - tooltipApproxHeight - 20 > 0;

        const contentHtml = `<div class="guidance-tooltip-box">${message}</div>`;

        this.tooltipEl.className = "guidance-tooltip";
        this.tooltipEl.innerHTML = contentHtml;
        this.tooltipEl.style.display = "flex";
        this.tooltipEl.style.flexDirection = "column";
        this.tooltipEl.style.alignItems = "center";
        this.tooltipEl.style.width = `${tooltipWidth}px`;
        this.tooltipEl.style.position = "absolute";
        this.tooltipEl.style.zIndex = "1000";
        this.tooltipEl.style.pointerEvents = "none";

        const left = centerX - tooltipWidth / 2 + window.scrollX;
        const top = placeAbove
            ? minY - tooltipApproxHeight - 16 + window.scrollY
            : maxY + 8 + window.scrollY;

        this.tooltipEl.style.left = `${Math.max(5, left)}px`;
        this.tooltipEl.style.top = `${top}px`;
    }
}

export const guidance = new GuidanceManager();

export function highlight(ids: string[]): void {
    guidance.highlight(ids);
}

export function showMessage(message: string): void {
    guidance.showMessage(message);
}

export function endGuidance(): void {
    guidance.stop();
}

// --- Guidance sequences ---

const KEYPAD_DIGITS = [
    "KEYPAD_1",
    "KEYPAD_2",
    "KEYPAD_3",
    "KEYPAD_4",
    "KEYPAD_5",
    "KEYPAD_6",
    "KEYPAD_7",
    "KEYPAD_8",
    "KEYPAD_9",
    "KEYPAD_0",
];

guidance.addGuidance("Set The Clock", {
    steps: [
        {
            advanceOn: [{ ids: ["SET_MAIN_CLOCK"] }],
            action: () => {
                clock.stop();
                clock.setMainClockVal(0);
                highlight(["SET_MAIN_CLOCK"]);
                showMessage("Press to set the game clock");
            },
        },
        {
            advanceOn: [{ ids: KEYPAD_DIGITS }],
            action: () => {
                highlight(["KEYPAD"]);
                showMessage("Set time, for example 1 5 0 0 0");
            },
        },
        {
            advanceOn: [{ ids: ["KEYPAD_ENTER"] }],
            action: () => {
                highlight(["KEYPAD", "KEYPAD_ENTER"]);
                showMessage("Press YES when done");
            },
        },
    ],
});

guidance.addGuidance("Start The Clock", {
    steps: [
        {
            advanceOn: [{ ids: ["START"] }],
            action: () => {
                highlight(["START"]);
                showMessage("Press to start the clock");
            },
        },
    ],
});

guidance.addGuidance("Stop The Clock", {
    steps: [
        {
            advanceOn: [{ ids: ["STOP"] }],
            action: () => {
                highlight(["STOP"]);
                showMessage("Press to stop the clock");
            },
        },
    ],
});

guidance.addGuidance("Add Penalty", {
    steps: [
        // Step 0: pick a side
        {
            advanceOn: [
                { ids: ["HOME_PLAYER_PENALTY"], nextStep: 1 },
                { ids: ["GUEST_PLAYER_PENALTY"], nextStep: 2 },
            ],
            action: () => {
                highlight(["HOME_PLAYER_PENALTY", "GUEST_PLAYER_PENALTY"]);
                showMessage("Home or Guest penalty?");
            },
        },
        // Step 1: home side selected — enter player number
        {
            advanceOn: [{ ids: KEYPAD_DIGITS, nextStep: 3 }],
            action: () => {
                highlight(["KEYPAD"]);
                showMessage("Enter Player #");
            },
        },
        // Step 2: guest side selected — enter player number
        {
            advanceOn: [{ ids: KEYPAD_DIGITS, nextStep: 3 }],
            action: () => {
                highlight(["KEYPAD"]);
                showMessage("Enter Player #");
            },
        },
        // Step 3: press YES when done (after player #)
        {
            advanceOn: [{ ids: ["KEYPAD_ENTER"] }],
            action: () => {
                highlight(["KEYPAD", "KEYPAD_ENTER"]);
                showMessage("Press YES when done");
            },
        },
        // Step 4: enter penalty time
        {
            advanceOn: [{ ids: KEYPAD_DIGITS }],
            action: () => {
                highlight(["KEYPAD"]);
                showMessage(
                    "Enter penalty time. A time may already be displayed"
                );
            },
        },
        // Step 5: press YES when done (after penalty time)
        {
            advanceOn: [{ ids: ["KEYPAD_ENTER"] }],
            action: () => {
                highlight(["KEYPAD", "KEYPAD_ENTER"]);
                showMessage("Press YES when done");
            },
        },
    ],
});

guidance.addGuidance("Advance Period", {
    steps: [
        {
            advanceOn: [{ ids: ["PERIOD_PLUS1"] }],
            action: () => {
                highlight(["PERIOD_PLUS1"]);
                showMessage("Press to advance the period");
            },
        },
    ],
});

guidance.addGuidance("Remove Shot", {
    steps: [
        {
            advanceOn: [
                { ids: ["HOME_SHOTS_MINUS1"] },
                { ids: ["GUEST_SHOTS_MINUS1"] },
            ],
            action: () => {
                highlight(["HOME_SHOTS_MINUS1", "GUEST_SHOTS_MINUS1"]);
                showMessage("Home or Guest shot to remove?");
            },
        },
    ],
});

guidance.addGuidance("Add Shot", {
    steps: [
        {
            advanceOn: [
                { ids: ["HOME_SHOTS_PLUS1"] },
                { ids: ["GUEST_SHOTS_PLUS1"] },
            ],
            action: () => {
                highlight(["HOME_SHOTS_PLUS1", "GUEST_SHOTS_PLUS1"]);
                showMessage("Home or Guest shot?");
            },
        },
    ],
});

guidance.addGuidance("Remove Score", {
    steps: [
        {
            advanceOn: [
                { ids: ["HOME_SCORE_MINUS1"] },
                { ids: ["GUEST_SCORE_MINUS1"] },
            ],
            action: () => {
                highlight(["HOME_SCORE_MINUS1", "GUEST_SCORE_MINUS1"]);
                showMessage("Home or Guest score to remove?");
            },
        },
    ],
});

guidance.addGuidance("Add Score", {
    steps: [
        {
            advanceOn: [
                { ids: ["HOME_SCORE_PLUS1"] },
                { ids: ["GUEST_SCORE_PLUS1"] },
            ],
            action: () => {
                highlight(["HOME_SCORE_PLUS1", "GUEST_SCORE_PLUS1"]);
                showMessage("Home or Guest score?");
            },
        },
    ],
});

